import {effect, inject, Injectable, signal} from '@angular/core';
import {addDoc, collection, deleteDoc, doc, DocumentData, Firestore, onSnapshot, query, QuerySnapshot, where} from 'firebase/firestore';
import {FirebaseInitService} from '../shared/firebase-init.service';
import {AlertController} from '@ionic/angular/standalone';
import {Invitation, InvitationNotif, Profile, Room} from '@models';
import {InvitationStatus} from '@enums';
import {NotificationService} from './notification.service';
import {ProfileService} from '../community';
import {RoomService} from './room.service';
import {SendmailService} from './sendmail.service';
import {AnalyticsService, logger, ToastService, TranslateHelperService} from '../shared';
import {generateIcebreakerSuggestions} from './icebreaker-suggestions.util';


@Injectable()
export class InvitationService {

	private readonly invitationCollection;
	private readonly invitationSend = signal<Invitation[]>([]);
	private readonly invitationReceived = signal<Invitation[]>([]);
	private notificationService;
	private roomService;
	private sendMailService;
	private profileService;
	private translateService: TranslateHelperService = inject(TranslateHelperService);
	private toastService;
	private analyticsService: AnalyticsService = inject(AnalyticsService);
	private readonly currentUid = signal<string>('');
	private unsubscribeSender?: () => void;
	private unsubscribeReceiver?: () => void;

	private readonly firebaseInit = inject(FirebaseInitService);
	private readonly firestore: Firestore = this.firebaseInit.firestore;

	private alertCtrl = inject(AlertController);

	constructor() {
		this.profileService = inject(ProfileService);
		this.invitationCollection = collection(this.firestore, `invitation`);
		this.notificationService = inject(NotificationService);
		this.roomService = inject(RoomService);
		this.sendMailService = inject(SendmailService);
		this.toastService = inject(ToastService);

		effect(() => {
			const uid = this.profileService.profile()?.uuid ?? '';
			if (uid === this.currentUid()) {
				return;
			}
			this.currentUid.set(uid);
			this.cleanupListeners();
			this.invitationSend.set([]);
			this.invitationReceived.set([]);
			if (!uid) {
				return;
			}
			this.unsubscribeSender = onSnapshot(this.getInvitationList('sender', uid), (snapshot: QuerySnapshot<DocumentData>) => {
				this.invitationSend.set(this.buildInvitations(snapshot));
			});
			this.unsubscribeReceiver = onSnapshot(this.getInvitationList('receiver', uid), (snapshot: QuerySnapshot<DocumentData>) => {
				this.invitationReceived.set(this.buildInvitations(snapshot));
			});
		});
	}

	getInvitationSended() {
		return this.invitationSend.asReadonly();
	}

	getInvitationReceived() {
		return this.invitationReceived.asReadonly();
	}

	updateInvitation(invit: Invitation) {
		if (invit.uuid) {
			if (invit.status === InvitationStatus.accepted) {
				return this.deleteInvitation(invit);
			} else {
				return deleteDoc(doc(this.firestore, `/invitation/${invit.uuid}`));
			}
		} else {
			return Promise.resolve();
		}

	}

	createInvitation(invit: Invitation) {
		invit.sender = invit.sender || this.profileService.currentUid;
		const newNotification = new InvitationNotif();
		newNotification.uuid = '';
		newNotification.inviteID = invit.sender;
		void this.profileService.getProfileByUid(invit.receiver)
			.then((profile) => {
				if (profile?.uuid) {
					this.notificationService.sendNotification('invitation', profile, newNotification);
					this.sendMailService.sendNewFriendToUser(profile);
				}
			})
			.catch((error: unknown) => logger.error(error));

		return addDoc(this.invitationCollection, invit.toFirebase()).then((docRef) => {
			this.analyticsService.trackFunnelStep('first_interaction', {
				source_page: 'search',
				target_page: 'messages',
				interaction_type: 'message'
			});
			return docRef;
		});
	}

	getInvitationList(type: string, uid: string) {
		return query(this.invitationCollection, where(type, '==', uid));
	}

	async sendInvite(selectedProfile: Profile) {
		if (await this.checkRelationSTatus(selectedProfile)) {
			const suggestions = generateIcebreakerSuggestions(this.profileService.profile(), selectedProfile);
			const escapeHtml = (value: string) => value
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
				.replace(/'/g, '&#39;');
			const suggestionMessage = [
				await this.translateService.instant('Invitation.Vous pouvez envoyer un seul message à cette personne, si elle vous accepte vous pourrez discuter librement'),
				'',
				'Suggestions personnalisées :',
				...suggestions.map((suggestion, index) => `${index + 1}. ${suggestion}`)
			].map((line) => escapeHtml(line)).join('<br>');

			const alert = await this.alertCtrl.create({
				                                          cssClass: 'my-custom-class',
				                                          header: await this.translateService.instant('Invitation.Invitation'),
				                                          message: suggestionMessage,
				                                          inputs: [
					                                          {
						                                          name: 'message',
						                                          type: 'textarea',
						                                          placeholder: await this.translateService.instant('Invitation.Votre message'),
						                                          value: suggestions[0]
					                                          }
				                                          ],
				                                          buttons: [
					                                          {
						                                          text: await this.translateService.instant('Cancel'),
						                                          role: 'cancel',
						                                          cssClass: 'secondary'
					                                          },
					                                          {
						                                          text: await this.translateService.instant('Envoyer'),
						                                          cssClass: 'secondary',
						                                          handler: async (data) => {
							                                          if (data.message.trim().length < 3) {
								                                          this.toastService
								                                              .create({
									                                                      message: await this.translateService.instant('Invitation.Votre invitation doit avoir au moins 3 caractères'),
									                                                      duration: 5000,
									                                                      cssClass: 'yourCssClassName errorToast'
								                                                      })
								                                              .then((toast: { present: () => Promise<void> }) => toast.present());
							                                          } else {
								                                              this.createInvitation(new Invitation(data.message.replace(/(\r\n|\r|\n){2,}/g, '$1\n'), selectedProfile.uuid))
								                                              .then(async () => {
									                                              const sentMessage = await this.translateService.instant('Invitation.Invitation envoyée à');
									                                              this.toastService.showToastSuccess(`${sentMessage} ${selectedProfile.username} !`);
								                                              })
								                                              .catch((error: unknown) => logger.error(error));
							                                          }
						                                          }
					                                          }
				                                          ]
			                                          });
			await alert.present();
		}
	}

	accepte(invitation: Invitation) {
		invitation.status = InvitationStatus.accepted;
		this.notificationService.deleteInvitationNotification(invitation.sender);
		this.updateInvitation(invitation).then();
		void this.profileService.getProfileByUid(invitation.sender)
			.then((user: Profile | undefined) => {
				if (user?.uuid) {
					user.friends.push(this.profileService.currentUid);
					this.profileService.update(user).then();
					this.profileService.profile()!.friends.push(invitation.sender);
					this.profileService.update().then();
					this.roomService.createRoom(new Room({
						                                     name: this.profileService.profile()!.username + ' ' + user.username,
						                                     isPrivate: true,
						                                     members: [invitation.sender, invitation.receiver]
					                                     })).then(async () => this.toastService.showToastSuccess(await this.translateService.instant('invit.accepted')));
				}
			})
			.catch((error: unknown) => logger.error(error));

	}

	denied(invitation: Invitation) {
		invitation.status = InvitationStatus.denied;
		this.updateInvitation(invitation).then();
		this.notificationService.deleteInvitationNotification(invitation.sender);
	}

	private async checkRelationSTatus(selectedProfile: Profile): Promise<boolean> {
		if (selectedProfile.uuid in this.profileService.profile()!.blocked) {
			this.toastService.showToastError(await this.translateService.instant('Vous avez bloqué cet utilisateur'));
			return false;
		} else if (this.profileService.currentUid in selectedProfile.blocked) {
			const [messageStart, messageEnd] = await Promise.all([
				this.translateService.instant('Invitation.As-tu déjà contacté user'),
				this.translateService.instant('Invitation.sur les salons, pour te faire connaitre?')
			]);
			const al = await this.alertCtrl.create({
				                                       cssClass: 'my-custom-class-error',
				                                       header: await this.translateService.instant('Invitation.Invitation déjà envoyée !'),
				                                       message: messageStart + ' ' + selectedProfile.username + ' ' + messageEnd
			                                       });
			await al.present();
			return false;
		} else {
			if (this.invitationSend().filter(i => i.receiver === selectedProfile.uuid).length) {
				const [messageStart, messageEnd] = await Promise.all([
					this.translateService.instant('Invitation.As-tu déjà contacté user'),
					this.translateService.instant('Invitation.sur les salons, pour te faire connaitre?')
				]);
				const al = await this.alertCtrl.create({
					                                       cssClass: 'my-custom-class-error',
					                                       header: await this.translateService.instant('Invitation.Invitation déjà envoyée !'),
					                                       message: messageStart + ' ' + selectedProfile.username + ' ' + messageEnd
				                                       });
				await al.present();
				return false;
			} else if (this.invitationReceived().filter(i => i.sender === selectedProfile.uuid).length) {
				const al = await this.alertCtrl.create({
					                                       cssClass: 'my-custom-class',
					                                       header: await this.translateService.instant('Invitation.It\'s a match !'),
					                                       message:
						                                       await this.translateService.instant('Invitation.Cet utilisateur vous a déjà envoyé un message, consultez vos invitations et acceptez la sienne afin de pouvoir discuter')
				                                       });
				await al.present();
				return false;
			}
		}
		return true;
	}

	private deleteInvitation(invit: Invitation) {
		return deleteDoc(doc(this.firestore, '/invitation/' + invit.uuid));
	}

	private cleanupListeners(): void {
		if (this.unsubscribeSender) {
			this.unsubscribeSender();
			this.unsubscribeSender = undefined;
		}
		if (this.unsubscribeReceiver) {
			this.unsubscribeReceiver();
			this.unsubscribeReceiver = undefined;
		}
	}

	private buildInvitations(snapshot: QuerySnapshot<DocumentData>): Invitation[] {
		const invitations: Invitation[] = [];
		snapshot.forEach((docSnapshot) => {
			const data = docSnapshot.data();
			if (!data || typeof data !== 'object') {
				return;
			}
			const invitationData = {
				...data as Record<string, unknown>,
				uuid: docSnapshot.id
			};
			invitations.push(Invitation.fromObject(invitationData) as unknown as Invitation);
		});
		return invitations;
	}
}
